import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { LANGUAGES_ARRAY } from "../../constants/languages";
import Wrapper from "../wrapper";
import React, { useEffect } from "react";
import GlobalWidgets from "../widgets/GlobalWidgets";
import { useReactiveVar } from "@apollo/client";
import {
  isHaveBeenBlocked,
  isPaymentError,
  isPublishShow,
  isShowNewProjectModal,
  isShowTemplateModal,
  isUpgradeShow,
  moveItem,
  projectLimitModal,
  publishLimitModal,
  renameFolder,
} from "../../graphql/state";
import { CustomToast } from "../inputs/toast";
import { useUserQuery } from "../../graphql/menu.hooks";
import useGetUrlLocale from "../../hooks/useGetUrlLocale";
import { userService } from "../../services/userService";
import RenderOnce from "../../utils/renderOnce";
import {
  UpgradePlanModalLazy,
  ProjectLimitModalLazy,
  PublishLimitModalLazy,
  PublishModalLazy,
  TemplateModalLazy,
  MoveModalLazy,
  RenameFolderModalLazy,
  CreateProjectModalLazy,
} from "../modals/lazyModals";

const PAGES_WITHOUT_MENU = ["/payment/successful"];

const Layout = () => {
  const { locale = "en" } = useParams();
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { data: userData, loading } = useUserQuery();
  const lng = useGetUrlLocale();

  const isShowUpgrade = useReactiveVar(isUpgradeShow);
  const publishModal = useReactiveVar(isPublishShow);
  const templateModal = useReactiveVar(isShowTemplateModal);
  const moveItemState = useReactiveVar(moveItem);
  const renameFolderState = useReactiveVar(renameFolder);
  const isPaymentErr = useReactiveVar(isPaymentError);
  const isShowProjectModal = useReactiveVar(isShowNewProjectModal);
  const isBlocked = useReactiveVar(isHaveBeenBlocked);
  const showProjectLimitModal = useReactiveVar(projectLimitModal);
  const showPublishLimitModal = useReactiveVar(publishLimitModal);

  useEffect(() => {
    if (LANGUAGES_ARRAY.includes(locale)) {
      i18n.changeLanguage(locale);
    } else {
      navigate("/", { replace: true });
    }
  }, [locale, i18n, navigate]);

  useEffect(() => {
    if (!userData?.user && !loading) {
      window.location.href = `${process.env.REACT_APP_ME_PAGE_URL}${lng}/login`;
    }

    if (userData?.user) userService.setUser(userData);
  }, [userData, loading, lng]);

  const isPageWithoutMenu = PAGES_WITHOUT_MENU.some((page) =>
    pathname.includes(page)
  );

  if (isPageWithoutMenu) {
    return <Outlet />;
  }

  return (
    <>
      <Wrapper>
        <GlobalWidgets />
        <Outlet />
      </Wrapper>
      <RenderOnce when={isShowUpgrade}>
        <UpgradePlanModalLazy
          show={isShowUpgrade}
          onCancel={() => isUpgradeShow(false)}
        />
      </RenderOnce>
      <RenderOnce when={showProjectLimitModal}>
        <ProjectLimitModalLazy />
      </RenderOnce>
      <RenderOnce when={showPublishLimitModal}>
        <PublishLimitModalLazy />
      </RenderOnce>
      <RenderOnce when={publishModal.isActive}>
        <PublishModalLazy
          show={publishModal.isActive}
          onCancel={() =>
            isPublishShow({ isActive: false, pageId: null, sharePage: false })
          }
        />
      </RenderOnce>
      <RenderOnce when={templateModal.isActive}>
        <TemplateModalLazy
          showModal={templateModal.isActive}
          handleCloseModal={() =>
            isShowTemplateModal({ isActive: false, page: null })
          }
        />
      </RenderOnce>
      <RenderOnce when={moveItemState.isActive}>
        <MoveModalLazy
          show={moveItemState.isActive}
          onCancel={() => moveItem({ ...moveItemState, isActive: false })}
        />
      </RenderOnce>
      <RenderOnce when={renameFolderState.isActive}>
        <RenameFolderModalLazy
          show={renameFolderState.isActive}
          onCancel={() => renameFolder({ isActive: false, folderId: null })}
        />
      </RenderOnce>
      {isPaymentErr && (
        <CustomToast
          isShow={isPaymentErr}
          setShow={() => isPaymentError(false)}
          classes="px-2 px-md-0"
          background="bg-danger"
          text="text-center text-white"
          message={`${t("payment.Something went wrong. Try again later or contact support.")}`}
        />
      )}
      {isBlocked && (
        <CustomToast
          isShow={isBlocked}
          setShow={() => isHaveBeenBlocked(false)}
          classes="px-2 px-md-0"
          background="bg-danger"
          text="text-center text-white"
          message={`${t("validation.Your account has been blocked")}`}
        />
      )}
      <RenderOnce when={!!isShowProjectModal}>
        <CreateProjectModalLazy
          showModal={isShowProjectModal}
          closeModal={() => isShowNewProjectModal(false)}
        />
      </RenderOnce>
    </>
  );
};

export default Layout;
